import { Button, Modal } from '@mantine/core';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  MemberDeletionBlockerDto,
  MemberDeletionBlockerDtoTypeEnum,
} from 'src/Services/API/generated';

import styles from './BlockedMemberDeletionModal.module.scss';
import { Props } from './BlockedMemberDeletionModal.types';

const SECTION_TITLES: Record<MemberDeletionBlockerDtoTypeEnum, string> = {
  [MemberDeletionBlockerDtoTypeEnum.Owner]: 'Propriétaire du bien',
  [MemberDeletionBlockerDtoTypeEnum.ExpenseShare]: 'Dépenses partagées',
  [MemberDeletionBlockerDtoTypeEnum.ExpensePaid]: 'Dépenses payées',
  [MemberDeletionBlockerDtoTypeEnum.ExpenseSettlement]: 'Règlements (dépenses)',
  [MemberDeletionBlockerDtoTypeEnum.MoneyTransfer]: "Transferts d'argent",
};

const SECTION_ORDER: MemberDeletionBlockerDtoTypeEnum[] = [
  MemberDeletionBlockerDtoTypeEnum.Owner,
  MemberDeletionBlockerDtoTypeEnum.ExpenseShare,
  MemberDeletionBlockerDtoTypeEnum.ExpensePaid,
  MemberDeletionBlockerDtoTypeEnum.ExpenseSettlement,
  MemberDeletionBlockerDtoTypeEnum.MoneyTransfer,
];

const buildBlockerRoute = (assetId: string, blocker: MemberDeletionBlockerDto): string | null => {
  switch (blocker.type) {
    case MemberDeletionBlockerDtoTypeEnum.ExpenseShare:
    case MemberDeletionBlockerDtoTypeEnum.ExpensePaid:
    case MemberDeletionBlockerDtoTypeEnum.ExpenseSettlement:
      return `/asset/${assetId}/expenses/${blocker.entityId}`;
    case MemberDeletionBlockerDtoTypeEnum.MoneyTransfer:
      return `/asset/${assetId}/expenses`;
    case MemberDeletionBlockerDtoTypeEnum.Owner:
    default:
      return null;
  }
};

const formatDate = (date?: Dayjs): string => (date ? date.format('L') : '');

const BlockedMemberDeletionModal = ({ open, onClose, memberName, blockers, assetId }: Props) => {
  const { t } = useTranslation();

  const grouped = SECTION_ORDER.map(type => ({
    type,
    items: blockers.filter(b => b.type === type),
  })).filter(group => group.items.length > 0);

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={t('admin.members.deletion.blockedTitle', 'Impossible de supprimer ce membre')}
      size="lg"
    >
      <div className={styles.intro}>
        {t(
          'admin.members.deletion.blockedIntro',
          "{{name}} est référencé dans les éléments suivants. Pour pouvoir le retirer du bien, résolvez ou supprimez d'abord ces éléments.",
          { name: memberName }
        )}
      </div>

      {grouped.map(group => (
        <div key={group.type} className={styles.section}>
          <div className={styles.sectionTitle}>
            {SECTION_TITLES[group.type]} ({group.items.length})
          </div>
          {group.items.map(blocker => {
            const route = buildBlockerRoute(assetId, blocker);
            const dateText = formatDate(blocker.date);

            if (route) {
              return (
                <Link
                  key={`${blocker.type}-${blocker.entityId}`}
                  to={route}
                  className={styles.blockerItem}
                  onClick={onClose}
                >
                  <span className={styles.blockerLabel}>{blocker.label}</span>
                  {dateText && <span className={styles.blockerDate}>{dateText}</span>}
                </Link>
              );
            }

            return (
              <div key={`${blocker.type}-${blocker.entityId}`} className={styles.staticBlocker}>
                {blocker.label}
              </div>
            );
          })}
        </div>
      ))}

      <div className={styles.buttons}>
        <Button onClick={onClose}>{t('common.understood', 'Compris')}</Button>
      </div>
    </Modal>
  );
};

export default BlockedMemberDeletionModal;
